import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { DecisionModel, EvalContext, Outcome } from '../models/decision-model';
import { DecisionEngineService } from '../_services/desigion-engine';
import { ActivatedRoute } from '@angular/router';
import { ModelBuilderService } from '../_services/model-builder.service';
function mustBeTrue(c: AbstractControl): ValidationErrors | null {
  return c.value === true ? null : { mustBeTrue: true };
}
@Component({
  selector: 'app-sut',
  templateUrl: './sut.component.html',
  styleUrls: ['./sut.component.css']
})
export class SutComponent implements OnInit {

title = 'SUT Karar Motoru – Dinamik';
  model: DecisionModel | null = null;
  result: Outcome | null = null;
  trace: {type: string; nodeId?: string; ok?: boolean; detail?: string}[] = [];
  currentStepIndex = 0;

  // Model seçenekleri (overlay)
  models = [
    { key: 'ibuprofen', label: 'Parenteral Ibuprofen', path: 'assets/models/model.ibuprofen.overlay.json' },
    { key: 'kalsiyum',  label: 'Kalsiyum Kanal Blokörü (Genel)', path: 'assets/models/model.kalsiyum.overlay.json' }
  ];
  selectedModelPath = this.models[0].path;

  // Pack/param bayrakları (UI görünürlüğü/validasyon için)
  flags = {
    needsDayCare: false,
    needsDoseCap: false,
    usesSupplyAny: false,
    hasCopayCheck: false,
    inpatientOnly: false
  };
  maxDailyDose = 4;

  // Superset form (tüm alanlar)
  form = this.fb.group({
    // STEP 1: Temel
    step1: this.fb.group({
      setting: this.fb.control<'outpatient'|'inpatient'>('outpatient', { nonNullable: true, validators: [Validators.required] }),
      daily_dose_present: this.fb.control(true, { nonNullable: true })
    }),

    // STEP 2: Tedarik / Rapor
    step2: this.fb.group({
      is_first_prescription: this.fb.control(true, { nonNullable: true }),
      supply_days: this.fb.control(30, { nonNullable: true, validators: [Validators.min(1)] }),

      // Rapor
      report_present: this.fb.control(false, { nonNullable: true }),
      issuer_specialty: this.fb.control<string | null>(null),
      issuer_institution: this.fb.control<string | null>('any'),
      validity_years: this.fb.control<number | null>(null),
      icd: this.fb.control<string | null>(null)
    }),

    // STEP 3: Hastane/Günübirlik + Endikasyon/Doz
    step3: this.fb.group({
      hospital_use: this.fb.control(false, { nonNullable: true }),
      is_day_care: this.fb.control(false, { nonNullable: true }),
      indication: this.fb.control<string | null>(null),
      daily_dose: this.fb.control<number | null>(null)
    }),

    // STEP 4: İstisnalar
    step4: this.fb.group({
      colored_rx: this.fb.control(false, { nonNullable: true }),
      off_label_approval: this.fb.control(false, { nonNullable: true }),
      imported_drug: this.fb.control(false, { nonNullable: true }),
      sut_exclusion_article: this.fb.control<string | null>(null),
      sgk_specific_excluded: this.fb.control(false, { nonNullable: true })
    })
  });

  constructor(
    private fb: FormBuilder,
    private engine: DecisionEngineService,
    private builder: ModelBuilderService
  ) {}

  ngOnInit(): void {
    this.loadOverlay(this.selectedModelPath);
    this.setupDynamicValidators();
  }

  // Overlay/model yükle
  loadOverlay(path: string) {
    this.builder.build(path).then(m => {
      this.model = m;
      this.currentStepIndex = 0;
      this.result = null;
      this.trace = [];
      this.computeFlagsFromModel();
      this.applyValidatorsByFlags();
    });
  }

  onChangeModel(path: string) {
    this.selectedModelPath = path;
    this.loadOverlay(path);
  }

  // Pack’lere göre UI bayrakları
  private computeFlagsFromModel() {
   const root: any = this.model?.tree as any;
    const children: any[] = Array.isArray(root?.children) ? root.children : [];
    const has = (id: string) => children.some(n => n?.id === id);
    const thenNode = (this.model?.tree as any)?.then;

    this.flags.inpatientOnly = has('inpatient_only');
    this.flags.needsDayCare = has('day_care_required');
    this.flags.usesSupplyAny = has('supply_any');
    this.flags.needsDoseCap = has('dose_cap') || has('dose_cap_optional');
    this.flags.hasCopayCheck = !!thenNode && (typeof thenNode === 'object') && thenNode.id === 'copay_check';

    // param: MAX_DAILY_DOSE
    this.maxDailyDose = Number(this.model?.__overlayParams?.MAX_DAILY_DOSE ?? 4);
  }

  // Dinamik validasyonlar
  private setupDynamicValidators() {
    // supply_days için (only if usesSupplyAny)
    const s2 = this.form.get('step2') as FormGroup;
    s2.get('is_first_prescription')!.valueChanges.subscribe(() => this.applyValidatorsByFlags());
    s2.get('report_present')!.valueChanges.subscribe(() => this.applyValidatorsByFlags());
  }

  private applyValidatorsByFlags() {
    // setting: inpatient_only ise otomatik inpatient yap
    if (this.flags.inpatientOnly) {
      this.form.get('step1.setting')!.setValue('inpatient');
    }

    // supply_days
    const s2 = this.form.get('step2') as FormGroup;
    const supplyCtrl = s2.get('supply_days')!;
    const validators = [Validators.min(1)];
    if (this.flags.usesSupplyAny) {
      const isFirst = !!s2.get('is_first_prescription')!.value;
      const reportPresent = !!s2.get('report_present')!.value;
      validators.push(Validators.required);
      validators.push(Validators.max(isFirst ? 31 : (reportPresent ? 93 : 31)));
    }
    supplyCtrl.setValidators(validators);
    supplyCtrl.updateValueAndValidity({ emitEvent: false });

    // daily_dose (doz kapaması)
    const s3 = this.form.get('step3') as FormGroup;
    const doseCtrl = s3.get('daily_dose')!;
    const dosePresentCtrl = this.form.get('step1.daily_dose_present')!;
    if (this.flags.needsDoseCap) {
      dosePresentCtrl.setValidators([Validators.requiredTrue]);
      doseCtrl.setValidators([Validators.required, Validators.min(1), Validators.max(this.maxDailyDose)]);
    } else {
      dosePresentCtrl.clearValidators();
      doseCtrl.clearValidators();
    }
    dosePresentCtrl.updateValueAndValidity({ emitEvent: false });
    doseCtrl.updateValueAndValidity({ emitEvent: false });
  }

  // Adımlar
  get visibleSteps() {
    return [
      { id: 'step1', title: '1) Temel', valid: () => this.form.get('step1')!.valid, visible: () => true },
      { id: 'step2', title: '2) Tedarik / Rapor', valid: () => this.form.get('step2')!.valid, visible: () => true },
      { id: 'step3', title: '3) Kurum / Endikasyon / Doz', valid: () => this.form.get('step3')!.valid, visible: () => true },
      { id: 'step4', title: '4) İstisnalar', valid: () => this.form.get('step4')!.valid, visible: () => true },
      { id: 'result', title: '5) Sonuç', valid: () => true, visible: () => true }
    ].filter(s => s.visible());
  }
  go(i: number) { if (i >= 0 && i < this.visibleSteps.length) this.currentStepIndex = i; }
  prev() { if (this.currentStepIndex > 0) this.currentStepIndex--; }
  next() {
    const cur = this.visibleSteps[this.currentStepIndex];
    if (!cur.valid()) return;
    if (this.currentStepIndex < this.visibleSteps.length - 1) {
      this.currentStepIndex++;
      if (this.visibleSteps[this.currentStepIndex].id === 'result') this.onEvaluate();
    }
  }

  // Evaluate
  onEvaluate() {
    if (!this.model) return;

    const s1 = this.form.get('step1') as FormGroup;
    const s2 = this.form.get('step2') as FormGroup;
    const s3 = this.form.get('step3') as FormGroup;
    const s4 = this.form.get('step4') as FormGroup;

    const documents = {
      keys: s2.get('report_present')!.value ? ['health_report'] : [],
      contains: (k: string) => (s2.get('report_present')!.value ? ['health_report'] : []).includes(k)
    };

    const ctx: EvalContext = {
      patient: {},
      prescriber: {},
      claim: {
        setting: s1.get('setting')!.value!,
        daily_dose_present: s1.get('daily_dose_present')!.value!,

        is_first_prescription: s2.get('is_first_prescription')!.value!,
        supply_days: s2.get('supply_days')!.value!,

        hospital_use: s3.get('hospital_use')!.value!,
        is_day_care: s3.get('is_day_care')!.value!,
        indication: s3.get('indication')!.value!,
        daily_dose: s3.get('daily_dose')!.value!,

        colored_rx: s4.get('colored_rx')!.value!,
        off_label_approval: s4.get('off_label_approval')!.value!,
        imported_drug: s4.get('imported_drug')!.value!,
        sut_exclusion_article: s4.get('sut_exclusion_article')!.value ?? null,
        sgk_specific_excluded: s4.get('sgk_specific_excluded')!.value!
      } as any,
      report: {
        present: s2.get('report_present')!.value!,
        issuer_specialty: s2.get('issuer_specialty')!.value,
        issuer_institution: s2.get('issuer_institution')!.value,
        validity_years: s2.get('validity_years')!.value,
        icd: s2.get('icd')!.value
      },
      documents,
      sut: {},
      lists: this.model?.lists ?? {}
    };

    const res = this.engine.evaluate(this.model, ctx);
    this.result = res.outcome;
    this.trace = res.trace;
  }

  // Template kısayollar
  get s1() { return this.form.get('step1') as FormGroup; }
  get s2() { return this.form.get('step2') as FormGroup; }
  get s3() { return this.form.get('step3') as FormGroup; }
  get s4() { return this.form.get('step4') as FormGroup; }
}